import React from "react";
import { connect, DispatchProp } from "react-redux";

import initialState from "core/reducers/initialState";
import { LinkTypeRegistry } from "./ILinkType";
import MenuTreeView, { IMenuNode, MenuTreeVariant } from "./MenuTree.view";
import INavMenuConfig from "./NavMenuConfig";
import PageLink from "./linkTypes/PageLink";
import UrlUtil from "core/util/UrlUtil";

/**
 * Wrapper component for {@link ./MenuTree.view} that maps static menu link config data
 * to menu tree nodes.
 *
 * @author chills
 */

interface INavMenuProps {
	menu: INavMenuConfig;
	expanded: { [id: string]: boolean; };
	onExpandChange: (id: string, isExpanded: boolean) => void;
	variant?: MenuTreeVariant;
	viewKey: string;
}

interface INavMenuStateProps {
	items: IMenuNode[];
}

type ResultProps = INavMenuStateProps & DispatchProp & INavMenuProps;

interface INodeDataWrapper {
	type: string;
	data: any;
}

function mapIdsToMenuNodes(state: any = initialState, ownProps: INavMenuProps, ids: string[]): Array<IMenuNode<INodeDataWrapper>> {

	let menuNodes = ids && ids.reduce<IMenuNode[]>((a, id) => {

		const linkData = ownProps.menu.data[id];

		if (linkData) {
			const linkType = LinkTypeRegistry.getType(linkData.type);

			if (linkType) {
				const node = linkType.mapDataToMenuNode(
					linkData.data,
					{
						id,
						children: mapIdsToMenuNodes(state, ownProps, linkData.children),
						isExpanded: !!ownProps.expanded && !!ownProps.expanded[id]
					},
					state
				);
				node['__type'] = linkType.type;
				if (node.isVisible !== false) {
					a.push(node);
				}
			}
		}

		return a;
	}, []);
	setLinkActiveForChildPages(menuNodes, state);
	return menuNodes;
}

function setLinkActiveForChildPages(menuNodes: IMenuNode<any>[], state: any) {
	let pageLinks = menuNodes && menuNodes.filter(it => it['__type'] === PageLink.type);
	if (pageLinks && pageLinks.length > 0 && pageLinks.every(it => !it.isActive)) { // nothing active
		let active: { id: string; pathname: string; } = null;
		pageLinks.forEach((it: any) => {
			if (UrlUtil.isPagePathActive(state, it.data.pagePath, true)) {
				const pathname = UrlUtil.getPagePathLink(state, it.data.pagePath);
				if (!active) { // set active only one link, set others not active 
					active = { id: it.id as string, pathname };
				} else {
					if (pathname.startsWith(active.pathname)) {
						active = { id: it.id as string, pathname };
					}
				}
			}
		});

		if (active) {
			menuNodes.forEach(it => {
				if (it['__type'] === PageLink.type) {
					if (it.id === active.id) {
						it.isActive = true;
					} else {
						it.isActive = false;
					}
				}
			});
		}
	}
}

function mapStateToProps(state: any = initialState, ownProps: INavMenuProps): INavMenuStateProps {

	return {
		items: mapIdsToMenuNodes(state, ownProps, ownProps.menu.ids)
	};
}


export default connect(mapStateToProps)(class NavMenu extends React.Component<ResultProps> {

	public render() {

		return (
			<MenuTreeView
				onExpandedStateChange={this.onExpandedStateChange}
				items={this.props.items}
				onSelectItem={this.onSelectItem}
				variant={this.props.variant}
				viewKey={this.props.viewKey}
			/>
		);
	}

	private onSelectItem = (item: IMenuNode) => {
		const type = item['__type'];
		if (!type) {
			return;
		}
		const linkType = LinkTypeRegistry.getType(type);
		if (linkType) {
			linkType.doNavigate(item.data, this.props.dispatch, item.id);
		}
	}

	private onExpandedStateChange = (item: IMenuNode, isExpanded: boolean) => {
		this.props.onExpandChange(item.id, isExpanded);
	}
});



// WEBPACK FOOTER //
// ./src/common/views/menuTree/NavMenu.tsx